/* eslint-disable react/prop-types */
import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { forgeClassHelper } from '../utils/classes';
import { useTheme } from '../Root/ThemeContext';
import { PortalContext, useNewPortalContext } from './PortalContext';
import ReactShadowRoot from 'react-shadow-root';

const classes = forgeClassHelper({ name: 'portal-provider', isPortal: true });

export type PortalProviderProps = {
  /** Passes a classname to the portalled element created by this component */
  className?: string;
  /** non-portalled children to receive the PortalContext */
  children?: ReactNode;
  /** A node representing where the Root component is mounted */
  rootNode: HTMLDivElement | null;
};

/** Creates a portal to document.body, preparing this environment to use Forge
 * styling.
 *
 * If being rendered within a shadow DOM, a new shadow root is created, and
 * all <style> and <link type="text/css"> elements are copied from the original
 * shadow root.
 */
function PortalProvider({ children, className, rootNode }: PortalProviderProps): ReactElement {
  const theme = useTheme();
  const [attachNode, setAttachNode] = useState<HTMLDivElement | null>(null);
  /** If mounted within a shadow DOM, this div is used to contain styles cloned
   * from a previous shadow DOM */
  const [styleNode, setStyleNode] = useState<HTMLDivElement | null>(null);
  const [mainEmotionNode, setMainEmotionNode] = useState<HTMLDivElement | null>(null);
  const [portalEmotionNode, setPortalEmotionNode] = useState<HTMLDivElement | null>(null);
  /** The shadow root to copy styles from
   *
   * Can have the following types:
   * * undefined - Initial render when portalNode hasn't been defined yet
   * * null - There is not, an never will be an original shadow root.
   * * ShadowRoot - The original shadow root has been resolved.
   */
  const originalShadowRoot = useMemo<ShadowRoot | null | undefined>(() => {
    if (typeof document === 'undefined') {
      // There are no portals in server side rendering.
      return null;
    } else if (rootNode === null) {
      // Need to wait for rootNode to be set
      return undefined;
    } else {
      // Return the shadow root if one exists
      const node = rootNode.getRootNode();
      return node === document ? null : (node as ShadowRoot);
    }
  }, [rootNode]);

  const portalContext = useNewPortalContext(attachNode, mainEmotionNode);

  const hasAdoptedStyleSheets =
    !!originalShadowRoot?.adoptedStyleSheets && originalShadowRoot.adoptedStyleSheets.length > 0;

  /** If mounted in a shadow DOM and constructable stylesheets are not available
   * in the original shadow root, copy all styles to a new shadow root */
  useEffect(() => {
    if (originalShadowRoot && !hasAdoptedStyleSheets && styleNode) {
      /** Find all non-emotion styles present in the original shadow DOM */
      const styleTags = originalShadowRoot.querySelectorAll('style:not([data-emotion])');
      const styleLinks = originalShadowRoot.querySelectorAll('link[type="text/css"]');

      /** Clone the styles onto a new shadow root */
      const clonedStyles = [...styleTags, ...styleLinks].map((node) => styleNode.appendChild(node.cloneNode(true)));

      /** Clean up styles on the off chance that a dependency changes */
      return () => {
        clonedStyles.forEach((node) => {
          node && styleNode.removeChild(node);
        });
      };
    }
  }, [hasAdoptedStyleSheets, originalShadowRoot, styleNode]);

  /** emotion-js can only create an emotion cache with a single container.
   * The problem is, Multiselect straddles two separate shadow DOMs. We need
   * to use a MutationObserver to copy <style> tags that get dynamically added
   * by emotion and clone them to the portal's shadow DOM.
   */
  useEffect(() => {
    if (mainEmotionNode && portalEmotionNode) {
      /** Clone a style node and add it to portalEmotionNode */
      const cloneNode = (node: Node): void => {
        const style = node as HTMLStyleElement;
        const isCloned = style.getAttribute('data-forge-cloned') !== null;
        /** Only clone the style element if it hasn't been cloned already. */
        if (!isCloned) {
          portalEmotionNode.appendChild(node.cloneNode(true));
          style.setAttribute('data-forge-cloned', '');
        }
      };

      /** Styles that may have been added while the relevant DOM nodes were resolving */
      const initialStyles = mainEmotionNode.querySelectorAll('style:not([data-forge-cloned])');
      initialStyles.forEach(cloneNode);

      /** Watch for mutations made
       *
       * We do not need to observe changes to attributes or style contents,
       * as emotion-js styles are purely additive
       */
      const styleObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutation) => {
          mutation.addedNodes.forEach(cloneNode);
        });
      });
      styleObserver.observe(mainEmotionNode, { childList: true });

      /** Clean up on unmount or on the off chance that a dependency changes.
       *
       * Cloned styles are not cleaned up given that if this useEffect were to
       * re-fire, the styles would not get re-cloned.
       */
      return () => {
        styleObserver.disconnect();
      };
    }
  }, [mainEmotionNode, portalEmotionNode]);

  /* Do not attempt to portal during server-side rendering in Gatsby,
   * or if we do not know whether we are in a shadow DOM yet.
   */
  if (typeof document === 'undefined' || originalShadowRoot === undefined) {
    return <PortalContext.Provider value={portalContext}>{children}</PortalContext.Provider>;
  } else if (originalShadowRoot === null) {
    // We are not mounted in a shadow DOM
    return (
      <PortalContext.Provider value={portalContext}>
        {ReactDOM.createPortal(
          <div ref={setAttachNode} {...classes({ theme: theme, extra: className })} />,
          document.body
        )}
        {children}
      </PortalContext.Provider>
    );
  } else {
    // We are mounted in a shadow DOM
    return (
      <PortalContext.Provider value={portalContext}>
        {ReactDOM.createPortal(
          <div {...classes({ element: 'shadow-dom' })} style={{ position: 'relative', contain: 'style' }}>
            <ReactShadowRoot mode="open" stylesheets={originalShadowRoot.adoptedStyleSheets}>
              <div ref={setStyleNode} {...classes({ element: 'static-styles' })} />
              <div ref={setPortalEmotionNode} {...classes({ element: 'portal-emotion' })} />
              <div ref={setAttachNode} {...classes({ theme: theme, extra: className })} />
            </ReactShadowRoot>
          </div>,
          document.body
        )}
        <div ref={setMainEmotionNode} {...classes({ element: 'main-emotion' })} />
        {children}
      </PortalContext.Provider>
    );
  }
}

export default PortalProvider;
